import React from 'react'
import { motion } from 'framer-motion'

// Static DKG logo (no animation) — suitable for embedding in modals, headers, etc.
export function DKGLogo({ width = 52, height = 57 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 78 49"
      style={{ shapeRendering: 'geometricPrecision', fillRule: 'evenodd', clipRule: 'evenodd' }}
      aria-label="DKG Logo"
    >
      <path fill="#f29400" d="M3.5,-0.5 H47.5 A4,4,0,0,1,51.5,3.5 V5.5 A4,4,0,0,1,47.5,9.5 H3.5 A4,4,0,0,1,-0.5,5.5 V3.5 A4,4,0,0,1,3.5,-0.5 Z" />
      <path fill="#f29400" d="M28.5,17.5 H71.5 A4,4,0,0,1,75.5,21.5 V23.5 A4,4,0,0,1,71.5,27.5 H28.5 A4,4,0,0,1,24.5,23.5 V21.5 A4,4,0,0,1,28.5,17.5 Z" />
      <path fill="#f19400" d="M18.5,34.5 H60.5 A4,4,0,0,1,64.5,38.5 V40.5 A4,4,0,0,1,60.5,44.5 H18.5 A4,4,0,0,1,14.5,40.5 V38.5 A4,4,0,0,1,18.5,34.5 Z" />
    </svg>
  )
}

export default function AnimatedLogo({ customLogo = null, spinnerColor = null } = {}) {
  if (customLogo) {
    const spinnerStroke = spinnerColor || '#f29400'
    return (
      <div className="flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center gap-4"
        >
          <img
            src={customLogo}
            alt="Logo"
            style={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain' }}
          />
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            aria-label="Loading"
            style={{ animation: 'spin_al 1s linear infinite' }}
          >
            <style>{`@keyframes spin_al { to { transform: rotate(360deg); } }`}</style>
            <circle
              cx="16" cy="16" r="12"
              fill="none"
              stroke={spinnerStroke}
              strokeOpacity="0.2"
              strokeWidth="3"
            />
            <path
              d="M16 4 a12 12 0 0 1 12 12"
              fill="none"
              stroke={spinnerStroke}
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        </motion.div>
      </div>
    )
  }

  const Logo = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="78px"
        height="85px"
        viewBox="0 0 78 49"
        style={{
          shapeRendering: 'geometricPrecision',
          textRendering: 'geometricPrecision',
          imageRendering: 'optimizeQuality',
          fillRule: 'evenodd',
          clipRule: 'evenodd'
        }}
        aria-label="Animated logo"
      >
        <style>
        {`
          @keyframes moveUp_2 {
            20%, 99% { transform: translateY(0); }
            31%, 90% { transform: translateY(-13px); }
          }

          @keyframes moveDown_2 {
            20%, 99% { transform: translateY(0); }
            31%, 90% { transform: translateY(13px); }
          }

          /* 
            The middle path will rotate only between 31% and 60%, 
            then hold that rotation until 90%, and reset by 100% 
            to sync with the next cycle.
          */
          @keyframes rotate_2 {
            0%,21% { transform: rotate(0deg); }
            22% { transform: rotate(0deg); }
            98% { transform: rotate(720deg); }
            100% { transform: rotate(720deg); }
          }

          .path1_2 {
            animation: moveUp_2 2.2s infinite ease-in-out;
          }

          .path2_2 {
            animation: rotate_2 2.2s infinite linear;
            transform-origin: center;
            transform-box: fill-box;
          }

          .path3_2 {
            animation: moveDown_2 2.2s infinite ease-in-out;
          }
        `}
        </style>
        <g>
          <path
            className="path1_2"
            fill="#f29400"
            d="
              M3.5,-0.5
              H47.5
              A4,4,0,0,1,51.5,3.5
              V5.5
              A4,4,0,0,1,47.5,9.5
              H3.5
              A4,4,0,0,1,-0.5,5.5
              V3.5
              A4,4,0,0,1,3.5,-0.5
              Z
            "
          />
        </g>
        <g>
          <path
            className="path2_2"
            fill="#f29400"
            d="
              M28.5,17.5
              H71.5
              A4,4,0,0,1,75.5,21.5
              V23.5
              A4,4,0,0,1,71.5,27.5
              H28.5
              A4,4,0,0,1,24.5,23.5
              V21.5
              A4,4,0,0,1,28.5,17.5
              Z
            "
          />
        </g>
        <g>
          <path
            className="path3_2"
            fill="#f19400"
            d="
              M18.5,34.5
              H60.5
              A4,4,0,0,1,64.5,38.5
              V40.5
              A4,4,0,0,1,60.5,44.5
              H18.5
              A4,4,0,0,1,14.5,40.5
              V38.5
              A4,4,0,0,1,18.5,34.5
              Z
            "
          />
        </g>
      </svg>
    )
  }

  return (
    <div
      className="flex items-center justify-center"
    >
      <motion.div
        initial={false}
        animate={{ scale: 1, opacity: 1 }}
        className="flex flex-col items-center"
      >
        <Logo />
      </motion.div>
    </div>
  )
}
